import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowUpRight, ArrowRight, Menu, X,
  ShieldCheck, Home as HomeIcon, TrendingUp, Rocket,
  Calculator, Compass, Building2,
  Radar, ShieldAlert, GitBranch, ListChecks, Sparkles, LineChart,
} from "lucide-react";
import { AltioraTreeLogo } from "@/components/brand/AltioraTreeLogo";
import { api } from "@/lib/api";
import { useDefaultLocation } from "@/hooks/useUserLocation";
import DashboardPreview from "@/components/landing/DashboardPreview";
import InteractiveDemo from "@/components/landing/InteractiveDemo";
import LiveSignalsSection from "@/components/landing/LiveSignalsSection";

// ─── Design tokens ────────────────────────────────────────────────────
const NAVY = "#0B2545";
const NAVY_DEEP = "#061528";
const CHAMPAGNE = "#C5A880";
const GOLD = "#B98F3D";
const IVORY = "#FBF6EA";
const CANVAS = "#FCFBF7";
const HAIRLINE = "#E8E1D0";
const INK = "#0F172A";
const INK_SOFT = "#334155";
const MUTED = "#64748B";


// ─── A/B testing ──────────────────────────────────────────────────────
function useCtaVariant() {
  const [variant, setVariant] = useState("A");
  useEffect(() => {
    let v = localStorage.getItem("altiora_cta_variant");
    if (v !== "A" && v !== "B") {
      v = Math.random() < 0.5 ? "A" : "B";
      localStorage.setItem("altiora_cta_variant", v);
    }
    setVariant(v);
  }, []);
  return variant;
}

function trackCta(variant, cta_key, action, label) {
  try {
    api.post("/landing/cta-event", {
      variant, cta_key, action, label, path: window.location.pathname,
    }).catch(() => {});
  } catch { /* ignore */ }
}

const CTA_VARIANTS = {
  A: "Découvrir ALTIORA",
  B: "Explorer la plateforme",
};


// ─── Motion primitives ────────────────────────────────────────────────
const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, ease: [0.16, 0.68, 0.24, 1] },
};

const stagger = (i = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, delay: 0.08 * i, ease: [0.16, 0.68, 0.24, 1] },
});


// ─── Reusable primitives ──────────────────────────────────────────────
function PrimaryCTA({ to, children, testid, external, onClick }) {
  const Cmp = external ? "a" : Link;
  const props = external ? { href: to } : { to };
  return (
    <Cmp
      {...props}
      onClick={onClick}
      data-testid={testid}
      className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium transition-all duration-300"
      style={{ background: INK, color: IVORY, letterSpacing: "0.01em" }}
    >
      <span>{children}</span>
      <ArrowRight
        className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5"
        strokeWidth={1.8}
      />
    </Cmp>
  );
}


function GhostCTA({ to, children, testid, external, onClick }) {
  const Cmp = external ? "a" : Link;
  const props = external ? { href: to } : { to };
  return (
    <Cmp
      {...props}
      onClick={onClick}
      data-testid={testid}
      className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium transition-all duration-300"
      style={{
        background: "transparent",
        color: INK,
        border: `1px solid ${HAIRLINE}`,
        letterSpacing: "0.01em",
      }}
    >
      <span>{children}</span>
      <ArrowUpRight
        className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        strokeWidth={1.8}
      />
    </Cmp>
  );
}


function Eyebrow({ children, testid, color = CHAMPAGNE }) {
  return (
    <div
      data-testid={testid}
      className="inline-flex items-center gap-2 text-[10px] font-medium uppercase tracking-[0.32em]"
      style={{ color }}
    >
      <span className="h-px w-6" style={{ background: color }} />
      {children}
    </div>
  );
}


// ─── Nav ──────────────────────────────────────────────────────────────
function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <nav
      className="sticky top-0 z-40 backdrop-blur-md border-b"
      style={{ background: `${CANVAS}dd`, borderColor: HAIRLINE }}
      data-testid="landing-nav"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-nav-logo">
          <AltioraTreeLogo size={26} />
          <span className="font-serif text-[17px] tracking-tight" style={{ color: INK }}>
            ALTIORA
          </span>
        </Link>
        <div className="hidden md:flex items-center gap-8 text-[13px]" style={{ color: INK_SOFT }}>
          <a href="#section-dashboard-preview" className="hover:opacity-70 transition-opacity">Produit</a>
          <a href="#section-interactive-demo" className="hover:opacity-70 transition-opacity">Démo</a>
          <a href="#audiences" className="hover:opacity-70 transition-opacity">À qui</a>
          <a href="#solution" className="hover:opacity-70 transition-opacity">Solution</a>
          <Link to="/manifeste" className="hover:opacity-70 transition-opacity">Manifeste</Link>
          <Link to="/login" className="hover:opacity-70 transition-opacity" data-testid="landing-nav-login">
            Se connecter
          </Link>
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden p-2"
          aria-label="Menu"
          data-testid="landing-nav-toggle"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>
      {open && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="md:hidden px-6 pb-4 flex flex-col gap-3 text-[15px]"
          style={{ color: INK_SOFT }}
        >
          <a href="#audiences" onClick={() => setOpen(false)}>À qui</a>
          <a href="#solution" onClick={() => setOpen(false)}>Solution</a>
          <a href="#vision" onClick={() => setOpen(false)}>Vision</a>
          <Link to="/manifeste" onClick={() => setOpen(false)}>Manifeste</Link>
          <Link to="/login" onClick={() => setOpen(false)}>Se connecter</Link>
        </motion.div>
      )}
    </nav>
  );
}


// ═════════════════════════════════════════════════════════════════════
// SECTION 1 · HERO — La promesse en 5 secondes
// ═════════════════════════════════════════════════════════════════════
function Hero() {
  const variant = useCtaVariant();
  const primaryLabel = CTA_VARIANTS[variant];
  const { label: locationLabel } = useDefaultLocation();

  useEffect(() => {
    if (!variant) return;
    trackCta(variant, "hero-primary", "view", primaryLabel);
  }, [variant, primaryLabel]);

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: CANVAS }}
      data-testid="hero-section"
    >
      <div
        aria-hidden
        className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full opacity-[0.07] blur-3xl pointer-events-none"
        style={{ background: `radial-gradient(circle, ${GOLD}, transparent 70%)` }}
      />
      <div className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-28 pb-20 md:pb-32 relative">
        <motion.div {...fadeUp}>
          <Eyebrow testid="hero-eyebrow">
            Intelligence décisionnelle{locationLabel ? ` · Depuis ${locationLabel}` : ""}
          </Eyebrow>
        </motion.div>

        <motion.h1
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          data-testid="hero-title"
          className="mt-8 font-serif leading-[1.04] tracking-tight max-w-4xl"
          style={{
            color: INK,
            fontSize: "clamp(2.4rem, 6vw, 4.8rem)",
            fontWeight: 400,
          }}
        >
          Chaque décision compte.
          <br />
          <span style={{ fontStyle: "italic", color: GOLD }}>
            ALTIORA vous aide à choisir la meilleure.
          </span>
        </motion.h1>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mt-10 md:mt-12 max-w-2xl text-[16px] md:text-[18px] leading-[1.7]"
          style={{ color: INK_SOFT }}
          data-testid="hero-subtitle"
        >
          L'intelligence décisionnelle qui aide les professionnels à
          <span style={{ color: INK, fontWeight: 500 }}> détecter les opportunités</span>,
          <span style={{ color: INK, fontWeight: 500 }}> anticiper les risques</span> et
          <span style={{ color: INK, fontWeight: 500 }}> prendre de meilleures décisions</span>,
          plus rapidement.
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.35 }}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center gap-3"
          data-testid="hero-ctas"
        >
          <PrimaryCTA
            to="/login"
            testid="hero-cta-primary"
            onClick={() => trackCta(variant, "hero-primary", "click", primaryLabel)}
          >
            {primaryLabel}
          </PrimaryCTA>
          <GhostCTA
            to="/demo"
            testid="hero-cta-secondary"
            onClick={() => trackCta(variant, "hero-secondary", "click", "Voir une démonstration")}
          >
            Voir une démonstration
          </GhostCTA>
        </motion.div>
      </div>
    </section>
  );
}


// ═════════════════════════════════════════════════════════════════════
// SECTION 2 · IDENTIFICATION IMMÉDIATE — à qui s'adresse ALTIORA
// ═════════════════════════════════════════════════════════════════════
function AudienceSection() {
  const audiences = [
    { icon: ShieldCheck, label: "Conseillers en sécurité financière",
      hint: "Recommandations, priorités, dossiers à haute valeur." },
    { icon: HomeIcon,   label: "Courtiers hypothécaires",
      hint: "Dossiers à qualifier, échéances à ne pas manquer." },
    { icon: TrendingUp, label: "Développement des affaires",
      hint: "Signaux d'opportunité, comptes clés, pipeline priorisé." },
    { icon: Rocket,     label: "Entrepreneurs",
      hint: "Arbitrages stratégiques, décisions à fort impact." },
    { icon: Calculator, label: "Comptables et fiscalistes",
      hint: "Optimisations à saisir, risques à anticiper." },
    { icon: Compass,    label: "Consultants",
      hint: "Recommandations structurées, livrables cadrés." },
    { icon: Building2,  label: "Institutions",
      hint: "Pilotage macro, benchmarks anonymisés, gouvernance." },
  ];
  return (
    <section
      id="audiences"
      className="border-t"
      style={{ borderColor: HAIRLINE, background: CANVAS }}
      data-testid="section-audiences"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <motion.div {...fadeUp}>
          <Eyebrow>Pour qui</Eyebrow>
        </motion.div>

        <motion.h2
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-8 font-serif leading-[1.1] tracking-tight max-w-3xl"
          style={{ color: INK, fontSize: "clamp(2rem, 4.4vw, 3.2rem)", fontWeight: 400 }}
          data-testid="section-audiences-title"
        >
          À qui s'adresse <span style={{ fontStyle: "italic", color: GOLD }}>ALTIORA</span> ?
        </motion.h2>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mt-6 max-w-2xl text-[15px] md:text-[16px] leading-[1.7]"
          style={{ color: INK_SOFT }}
        >
          Un professionnel prend en moyenne 35 000 décisions par jour.
          ALTIORA se concentre sur celles qui comptent vraiment.
        </motion.p>

        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {audiences.map((a, i) => (
            <motion.div
              key={a.label}
              {...stagger(i)}
              data-testid={`audience-${i}`}
              whileHover={{ y: -4, transition: { duration: 0.25 } }}
              className="group rounded-xl p-6 border transition-colors duration-300 cursor-default"
              style={{
                background: IVORY,
                borderColor: `${CHAMPAGNE}33`,
              }}
            >
              <div className="flex items-center justify-between">
                <div
                  className="w-10 h-10 rounded-md flex items-center justify-center transition-colors duration-300"
                  style={{ background: `${CHAMPAGNE}22`, color: NAVY }}
                >
                  <a.icon size={18} strokeWidth={1.6} />
                </div>
                <ArrowUpRight
                  className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  strokeWidth={1.8}
                  style={{ color: GOLD }}
                />
              </div>
              <div
                className="mt-5 font-serif text-[19px] md:text-[20px] leading-tight tracking-tight"
                style={{ color: INK }}
              >
                {a.label}
              </div>
              <div className="mt-3 text-[13.5px] leading-relaxed" style={{ color: INK_SOFT }}>
                {a.hint}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ═════════════════════════════════════════════════════════════════════
// SECTION 3 · LE PROBLÈME — pourquoi une intelligence décisionnelle
// ═════════════════════════════════════════════════════════════════════
function ProblemSection() {
  return (
    <section
      className="relative"
      style={{ background: NAVY_DEEP, color: IVORY }}
      data-testid="section-problem"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 70% 30%, ${GOLD}, transparent 60%)` }}
      />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-36 relative">
        <motion.div {...fadeUp}>
          <Eyebrow color={GOLD}>Le problème</Eyebrow>
        </motion.div>

        <motion.h2
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-8 font-serif leading-[1.15] tracking-tight max-w-4xl"
          style={{ color: IVORY, fontSize: "clamp(1.9rem, 4.2vw, 3rem)", fontWeight: 400 }}
          data-testid="section-problem-title"
        >
          Chaque jour, des décisions créent de la valeur…
          <br />
          <span style={{ fontStyle: "italic", color: GOLD }}>
            …et d'autres coûtent du temps, de l'argent et des opportunités.
          </span>
        </motion.h2>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-x-14 gap-y-8 max-w-4xl">
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="text-[16px] md:text-[17px] leading-[1.8]"
            style={{ color: `${IVORY}CC` }}
          >
            Les professionnels disposent de nombreuses informations, mais
            rarement d'un système capable de les transformer en décisions
            concrètes.
          </motion.p>
          <motion.p
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.28 }}
            className="text-[16px] md:text-[17px] leading-[1.8]"
            style={{ color: IVORY, fontWeight: 500 }}
          >
            C'est précisément la mission d'ALTIORA :
            transformer votre matière informationnelle en meilleure décision, chaque jour.
          </motion.p>
        </div>
      </div>
    </section>
  );
}


// ═════════════════════════════════════════════════════════════════════
// SECTION 4 · LA SOLUTION — modules concrets
// ═════════════════════════════════════════════════════════════════════
function SolutionSection() {
  const modules = [
    { icon: Radar,       label: "Détection d'opportunités",
      hint: "ALTIORA scanne vos signaux et fait remonter les moments-clés." },
    { icon: ShieldAlert, label: "Analyse des risques",
      hint: "Un tableau clair des expositions, des priorités d'action." },
    { icon: GitBranch,   label: "Simulateur de décisions",
      hint: "Compare deux trajectoires — impact chiffré immédiat." },
    { icon: ListChecks,  label: "Priorisation des actions",
      hint: "Le geste à haute valeur du jour, jamais celui d'à côté." },
    { icon: Sparkles,    label: "Recommandations intelligentes",
      hint: "Personnalisées à votre métier, votre style, votre pipeline." },
    { icon: LineChart,   label: "Suivi de progression",
      hint: "Une trajectoire visible sur 3 mois, 6 mois, 3 ans." },
  ];
  return (
    <section
      id="solution"
      className="border-t"
      style={{ borderColor: HAIRLINE, background: CANVAS }}
      data-testid="section-solution"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-36">
        <motion.div {...fadeUp}>
          <Eyebrow>La solution</Eyebrow>
        </motion.div>

        <motion.h2
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-8 font-serif leading-[1.1] tracking-tight max-w-4xl"
          style={{ color: INK, fontSize: "clamp(2rem, 4.4vw, 3.2rem)", fontWeight: 400 }}
          data-testid="section-solution-title"
        >
          Une intelligence décisionnelle
          <br />
          <span style={{ fontStyle: "italic", color: GOLD }}>
            au service de vos décisions.
          </span>
        </motion.h2>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((m, i) => (
            <motion.div
              key={m.label}
              {...stagger(i)}
              data-testid={`module-${i}`}
              className="rounded-xl p-6 border transition-shadow duration-300 hover:shadow-md"
              style={{ background: "#FFFFFF", borderColor: HAIRLINE }}
            >
              <div
                className="w-11 h-11 rounded-md flex items-center justify-center"
                style={{ background: `${NAVY}0F`, color: NAVY }}
              >
                <m.icon size={19} strokeWidth={1.6} />
              </div>
              <div
                className="mt-5 font-serif text-[20px] leading-tight tracking-tight"
                style={{ color: INK }}
              >
                {m.label}
              </div>
              <div className="mt-3 text-[14px] leading-relaxed" style={{ color: INK_SOFT }}>
                {m.hint}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}


// ═════════════════════════════════════════════════════════════════════
// SECTION 5 · STRATÉGIE DE DÉPLOIEMENT — aujourd'hui / demain
// ═════════════════════════════════════════════════════════════════════
function DeploymentStrategy() {
  return (
    <section
      className="border-t"
      style={{ borderColor: HAIRLINE, background: IVORY }}
      data-testid="section-deployment"
    >
      <div className="max-w-5xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <motion.div {...fadeUp}>
          <Eyebrow>Stratégie</Eyebrow>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16">
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}
                      data-testid="deployment-today">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em]"
                 style={{ color: CHAMPAGNE }}>
              Aujourd'hui
            </div>
            <div className="mt-4 font-serif text-[22px] md:text-[26px] leading-tight tracking-tight"
                 style={{ color: INK }}>
              Utilisé par les professionnels des services financiers et du développement d'affaires.
            </div>
          </motion.div>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}
                      data-testid="deployment-tomorrow">
            <div className="font-mono text-[11px] uppercase tracking-[0.28em]"
                 style={{ color: GOLD }}>
              Demain
            </div>
            <div className="mt-4 font-serif text-[22px] md:text-[26px] leading-tight tracking-tight"
                 style={{ color: INK }}>
              <span style={{ fontStyle: "italic", color: GOLD }}>Par tous les professionnels</span> qui
              prennent des décisions à fort impact.
            </div>
          </motion.div>
        </div>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.3 }}
          className="mt-16 max-w-2xl text-[15px] md:text-[16px] leading-[1.8]"
          style={{ color: INK_SOFT }}
        >
          Parce que les meilleures plateformes commencent par résoudre un
          problème réel avant d'étendre progressivement leur intelligence.
        </motion.p>
      </div>
    </section>
  );
}


// ═════════════════════════════════════════════════════════════════════
// SECTION 6 · VISION — confirmation après compréhension
// ═════════════════════════════════════════════════════════════════════
function VisionSection() {
  return (
    <section
      id="vision"
      className="relative"
      style={{ background: NAVY_DEEP, color: IVORY }}
      data-testid="section-vision"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{ background: `radial-gradient(ellipse at 30% 20%, ${GOLD}, transparent 60%)` }}
      />
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 md:py-40 relative">
        <motion.div {...fadeUp}>
          <Eyebrow color={GOLD}>Notre vision</Eyebrow>
        </motion.div>

        <motion.h2
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.1 }}
          className="mt-8 font-serif leading-[1.08] tracking-tight max-w-4xl"
          style={{ color: IVORY, fontSize: "clamp(2.2rem, 5vw, 4rem)", fontWeight: 400 }}
          data-testid="vision-title"
        >
          Aujourd'hui, toutes les organisations
          <br />
          disposent de systèmes d'information.
          <br />
          <span style={{ fontStyle: "italic", color: GOLD }}>
            Demain, elles auront aussi besoin
            <br />
            de systèmes d'aide à la décision.
          </span>
        </motion.h2>

        <motion.p
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mt-12 max-w-2xl text-[16px] md:text-[18px] leading-[1.7]"
          style={{ color: `${IVORY}cc` }}
          data-testid="vision-body"
        >
          ALTIORA a l'ambition de devenir cette intelligence décisionnelle de
          référence pour les professionnels, les organisations et les
          institutions.
        </motion.p>

        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.3 }}
          className="mt-14 flex flex-col sm:flex-row items-start sm:items-center gap-3"
          data-testid="vision-ctas"
        >
          <Link
            to="/login"
            data-testid="vision-cta-primary"
            onClick={() => trackCta("A", "vision-primary", "click", "Découvrir la plateforme")}
            className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium transition-all"
            style={{ background: IVORY, color: NAVY_DEEP, letterSpacing: "0.01em" }}
          >
            Découvrir la plateforme
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" strokeWidth={1.8} />
          </Link>
          <Link
            to="/demo"
            data-testid="vision-cta-secondary"
            onClick={() => trackCta("A", "vision-secondary", "click", "Demander une démonstration")}
            className="group inline-flex items-center gap-2 rounded-full px-6 py-3 text-[14px] font-medium transition-all"
            style={{
              background: "transparent",
              color: IVORY,
              border: `1px solid ${IVORY}44`,
              letterSpacing: "0.01em",
            }}
          >
            Demander une démonstration
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.8} />
          </Link>
          <Link
            to="/manifeste"
            data-testid="vision-cta-manifeste"
            className="ml-0 sm:ml-2 inline-flex items-center gap-1.5 text-[13px] font-medium"
            style={{ color: GOLD }}
          >
            Lire le manifeste <ArrowUpRight className="h-3.5 w-3.5" strokeWidth={1.8} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}


// ─── Footer ───────────────────────────────────────────────────────────
function Footer() {
  const { label: locationLabel } = useDefaultLocation();
  return (
    <footer
      className="border-t"
      style={{ borderColor: HAIRLINE, background: CANVAS, color: MUTED }}
      data-testid="landing-footer"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-14">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-center gap-2.5">
            <AltioraTreeLogo size={22} />
            <span className="font-serif text-[15px] tracking-tight" style={{ color: INK }}>
              ALTIORA
            </span>
          </div>
          <div className="text-[12px]">
            © {new Date().getFullYear()} ALTIORA Coop{locationLabel ? ` · ${locationLabel}` : ""} · Une intelligence décisionnelle pour les professionnels.
          </div>
          <div className="flex items-center gap-6 text-[13px]">
            <Link to="/manifeste" style={{ color: INK_SOFT }}>Manifeste</Link>
            <Link to="/login" style={{ color: INK_SOFT }}>Se connecter</Link>
            <a href="mailto:hello@altioracoop.com" style={{ color: INK_SOFT }}>Nous contacter</a>
          </div>
        </div>
      </div>
    </footer>
  );
}


// ─── Page ─────────────────────────────────────────────────────────────
export default function Landing() {
  return (
    <div
      className="min-h-screen"
      style={{
        background: CANVAS,
        fontFamily:
          '"Cormorant Garamond", "Playfair Display", ui-serif, Georgia, serif',
      }}
      data-testid="landing-root"
    >
      <div style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Inter, sans-serif" }}>
        <Nav />
        {/* 1. HERO — La promesse */}
        <Hero />
        {/* 2. DASHBOARD PREVIEW — Montrer le produit */}
        <DashboardPreview />
        {/* 3. INTERACTIVE DEMO — Voir ALTIORA raisonner */}
        <InteractiveDemo />
        {/* 4. IDENTIFICATION — Pour qui */}
        <AudienceSection />
        {/* 5. PROBLÈME — Pourquoi */}
        <ProblemSection />
        {/* 6. SOLUTION — Comment ça fonctionne */}
        <SolutionSection />
        {/* 7. LIVE SIGNALS — Preuve que la plateforme est vivante */}
        <LiveSignalsSection />
        {/* 8. STRATÉGIE DE DÉPLOIEMENT — Aujourd'hui / Demain */}
        <DeploymentStrategy />
        {/* 9. VISION — La confirmation */}
        <VisionSection />
        <Footer />
      </div>
    </div>
  );
}
